import { 
  Plus, 
  MessageSquare, 
  Trash2, 
  User, 
  LogOut,
  Menu,
  BarChart3,
  Sparkles,
  Settings,
  Library
} from 'lucide-react';
import { cn, formatTime } from '@/utils/helpers';
import { useChatStore, useAuthStore, useUIStore } from '@/store';
import { useNavigate } from 'react-router-dom';

export function Sidebar() {
  const navigate = useNavigate();
  const { 
    sessions, 
    currentSessionId, 
    createSession, 
    setCurrentSession, 
    deleteSession
  } = useChatStore();
  const { user, clearAuth } = useAuthStore();
  const { sidebarOpen, toggleSidebar } = useUIStore();

  const handleNewChat = () => {
    createSession();
  };

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (confirm('确定要删除这个会话吗？')) {
      deleteSession(id);
    }
  };

  const handleLogout = () => {
    if (confirm('确定要退出登录吗？')) {
      clearAuth();
    }
  };

  return (
    <>
      {/* 移动端遮罩 */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed lg:static inset-y-0 left-0 z-50',
          'bg-gray-900 text-white',
          'flex flex-col',
          'transition-all duration-300 ease-in-out',
          'w-64',
          !sidebarOpen && '-translate-x-full lg:translate-x-0'
        )}
      >
        {/* 头部 */}
        <div className="p-4 border-b border-gray-800">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center">
              <Sparkles size={18} className="text-white" />
            </div>
            <span className="font-bold text-lg">栩栩AI</span>
          </div>
          
          <button
            onClick={handleNewChat}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors font-medium"
          >
            <Plus size={18} />
            新建会话
          </button>
        </div>

        {/* 会话列表 */}
        <div className="flex-1 overflow-y-auto p-2">
          <div className="text-xs text-gray-500 uppercase font-medium px-2 mb-2">
            最近会话
          </div>
          
          {sessions.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              暂无会话
            </div>
          ) : (
            <div className="space-y-1">
              {sessions.map((session) => (
                <div
                  key={session.id}
                  onClick={() => setCurrentSession(session.id)}
                  className={cn(
                    'group flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors',
                    currentSessionId === session.id
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-300 hover:bg-gray-800/50'
                  )}
                >
                  <MessageSquare size={16} className="flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-sm font-medium">
                      {session.title}
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatTime(session.updated_at)}
                    </div>
                  </div>
                  <button
                    onClick={(e) => handleDelete(e, session.id)}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-700 rounded transition-all"
                    title="删除会话"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 底部 */}
        <div className="p-4 border-t border-gray-800 space-y-2">
          {/* 内容库 */}
          <button
            onClick={() => navigate('/content')}
            className="w-full flex items-center gap-3 px-3 py-2 text-gray-300 hover:bg-gray-800 rounded-lg transition-colors text-sm"
          >
            <Library size={18} />
            内容库
          </button>

          {/* 使用统计 */}
          <button
            onClick={() => window.location.href = '/stats'}
            className="w-full flex items-center gap-3 px-3 py-2 text-gray-300 hover:bg-gray-800 rounded-lg transition-colors text-sm"
          >
            <BarChart3 size={18} />
            使用统计
          </button>

          {/* 管理后台 - 仅管理员可见 */}
          {user?.role === 'admin' && (
            <button
              onClick={() => window.location.href = '/admin'}
              className="w-full flex items-center gap-3 px-3 py-2 text-gray-300 hover:bg-gray-800 rounded-lg transition-colors text-sm"
            >
              <Settings size={18} />
              管理后台
            </button>
          )}

          {/* 用户信息 */}
          {user && (
            <div className="pt-2 border-t border-gray-800">
              <div className="flex items-center gap-3 px-3 py-2">
                <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
                  <User size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{user.username}</div>
                  <div className="text-xs text-gray-500">
                    剩余 {user.api_quota - user.api_used} 次
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                  title="退出登录"
                >
                  <LogOut size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* 移动端菜单按钮 */}
      <button
        onClick={toggleSidebar}
        className={cn(
          'fixed top-4 left-4 z-50 p-2 rounded-lg lg:hidden',
          'bg-white shadow-md border border-gray-200',
          sidebarOpen && 'opacity-0 pointer-events-none'
        )}
      >
        <Menu size={20} />
      </button>
    </>
  );
}
